<template>
  <div
    class="m-swipe"
    :class="[direction]"
  >
    <div
      class="m-swipe-wrap"
      ref="swipeWrap"
      :style="{
        'transform' : 'translate3d(' + translateX + 'px,' + translateY + 'px, 0)',
        'transition-duration': transitionDuration + 'ms'
      }"
      @transitionend="onTransitionEnd"
      :page="page"
    >
      <slot></slot>
    </div>
    <div
      class="m-swipe-pagination"
      v-if="showIndicators"
    >
      <span
        class="m-swipe-pagination-bullet"
        v-for="(slide, index) in swipeChilds"
        :class="{'active': index + 1 === currentPage}"
        :key="index"
      >
      </span>
  </div>
  <slot name="indicators" :currentPage="currentPage" :total="swipeChilds.length"></slot>
  </div>
</template>
<script>
export default {
  name: 'swipe',
  props: {
    initPage: Number,
    direction: {
      type: String,
      default: 'horizontal',
      validator: value => ['vertical', 'horizontal'].indexOf(value) > -1,
    },
    slidesPerView: {
      type: Number,
      default: 1,
    },
    showIndicators: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    speed: {
      type: Number,
      default: 300,
    },
    boundary: {
      type: Number,
      default: 60,
    },
    auto: {
      type: Number,
      default: 0,
    },
    dragable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      lastPage: 1,
      translateX: 0,
      translateY: 0,
      startTranslate: 0,
      distance: 0,
      startPos: null,
      swipeChilds: [],
      translateOffset: 0,
      transitionDuration: 0,
      autoplayTimeoutId: null,
      continuous: false,
      preWidth: 0,
      isHorizontal: this.direction === 'horizontal',
      page: 0,
    }
  },
  created() {
    if (this.initPage !== undefined) {
      this.currentPage = this.initPage
    }
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    const { swipeWrap } = this.$refs
    swipeWrap.removeEventListener('touchstart', this.onTouchStart)
    swipeWrap.removeEventListener('touchmove', this.onTouchMove)
    swipeWrap.removeEventListener('touchend', this.onTouchEnd)
    this.pauseAutoplay()
  },
  watch: {
    loop(val, oldVal) {
      if (val !== oldVal) {
        this.continuous = val
      }
    },
  },
  methods: {
    init() {
      const { swipeWrap } = this.$refs
      this.swipeChilds = [].slice.call(swipeWrap.children)
      this.continuous = this.loop

      if (this.swipeChilds.length < this.slidesPerView) {
        this.continuous = false
      }

      if (this.continuous) {
        this.$nextTick(() => {
          this.createLoop()
          this.setPage(this.currentPage)
        })
      } else {
        this.setPage(this.currentPage)
      }

      if (this.auto > 0) {
        this.autoplay()
      }

      if (this.slidesPerView > 1) {
        this.preWidth = (swipeWrap.offsetWidth / this.slidesPerView)
        this.swipeChilds.forEach((item) => {
          item.style.width = `${this.preWidth}px`
        })
      }

      if (this.dragable) {
        swipeWrap.addEventListener('touchstart', this.onTouchStart)
        swipeWrap.addEventListener('touchmove', this.onTouchMove)
        swipeWrap.addEventListener('touchend', this.onTouchEnd)
      }
    },
    autoplay() {
      this.autoplayTimeoutId = setInterval(() => {
        this.next()
      }, this.auto)
    },
    pauseAutoplay() {
      if (this.autoplayTimeoutId) {
        clearInterval(this.autoplayTimeoutId)
        this.autoplayTimeoutId = null
      }
    },
    setPage(page) {
      this.lastPage = this.currentPage
      if (page === 0) {
        this.currentPage = this.swipeChilds.length
      } else if (page === this.swipeChilds.length + 1) {
        this.currentPage = 1
      } else {
        this.currentPage = page
      }

      if (this.continuous) {
        if (this.distance === 0) {
          this.setTranslate(this.getTranslateOfPage(this.lastPage))
        }

        setTimeout(() => {
          this.setTranslate(this.getTranslateOfPage(page))
          this.onTransitionStart()
        }, 0)
      } else {
        this.setTranslate(this.getTranslateOfPage(page))
        this.onTransitionStart()
      }
    },
    onTouchStart(e) {
      // e.preventDefault()
      this.startPos = this.getTouchPos(e)
      this.distance = 0
      this.startTime = Date.now()
      this.startTranslate = this.getTranslateOfPage(this.currentPage)
      this.transitionDuration = 0
    },
    onTouchMove(e) {
      this.pauseAutoplay()
      this.distance = this.getTouchPos(e) - this.startPos
      this.setTranslate(this.startTranslate + this.distance)

      if (Math.abs(this.distance) > 0) {
        e.preventDefault()
      }
    },
    onTouchEnd() {
      this.transitionDuration = this.speed
      const boundary = this.boundary / this.slidesPerView

      if (this.distance === 0) {
        return
      }
      if (this.distance < -boundary) {
        this.next()
      } else if (this.distance > boundary) {
        this.prev()
      } else {
        this.revert()
      }

      if (this.auto && !this.autoplayTimeoutId) {
        this.autoplay()
      }
    },
    onTransitionStart() {
      this.transitionDuration = this.speed
    },
    onTransitionEnd() {
      this.transitionDuration = 0
      this.distance = 0
      if (this.isPageChanged()) {
        this.$emit('on-change', this.currentPage)
      }
    },
    next() {
      const page = this.currentPage
      if ((page < this.swipeChilds.length) || this.continuous) {
        this.setPage(page + 1)
      } else {
        this.revert()
      }
    },
    prev() {
      const page = this.currentPage
      if (page > 1 || this.continuous) {
        this.setPage(page - 1)
      } else {
        this.revert()
      }
    },
    revert() {
      this.setPage(this.currentPage)
    },
    isPageChanged() {
      return this.lastPage !== this.currentPage
    },
    getTranslateOfPage(page) {
      const propName = this.isHorizontal ? 'clientWidth' : 'clientHeight'
      if (page === 0) {
        if (this.slidesPerView > 1) {
          return -(this.preWidth * (this.swipeChilds.length - 2))
        }
        return 0
      }

      return -[].slice.call(this.swipeChilds)
        .reduce((acc, el, i) => (i > page - 2 ? acc : acc + el[propName]), 0)
      + this.translateOffset
    },
    setTranslate(value) {
      const translateName = this.isHorizontal ? 'translateX' : 'translateY'
      this[translateName] = value
    },
    getTranslate() {
      const translateName = this.isHorizontal ? 'translateX' : 'translateY'
      return this[translateName]
    },
    getTouchPos(e) {
      const key = this.isHorizontal ? 'pageX' : 'pageY'
      return e.changedTouches ? e.changedTouches[0][key] : e[key]
    },
    createLoop() {
      const { swipeWrap } = this.$refs
      const propName = this.isHorizontal ? 'clientWidth' : 'clientHeight'

      if (this.slidesPerView > 1 && this.isHorizontal) {
        const { length } = this.swipeChilds
        for (let i = 0; i < length - 1; i += 1) {
          swipeWrap.appendChild(this.swipeChilds[i].cloneNode(true))
        }
        for (let i = length - 1; i >= 1; i -= 1) {
          swipeWrap.insertBefore(this.swipeChilds[i].cloneNode(true), swipeWrap.firstElementChild)
        }
        this.translateOffset = -swipeWrap.firstElementChild.offsetWidth * (length - 1)
      } else {
        const duplicateFirstChild = swipeWrap.firstElementChild.cloneNode(true)
        const duplicateLastChild = swipeWrap.lastElementChild.cloneNode(true)
        swipeWrap.insertBefore(duplicateLastChild, swipeWrap.firstElementChild)
        swipeWrap.appendChild(duplicateFirstChild)
        this.translateOffset = -duplicateFirstChild[propName]
      }
    },
  },
}
</script>
<style lang="scss" src="./index.scss"></style>
